/**
 * Entity Data Processing — Convenience Wrappers
 *
 * Mutation handler factory and validation utility built on top of the
 * for-API processor.
 */

import type {
  BaseEntity,
  SimpleFieldConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import { processEntityDataForApi } from "./for-api";

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Create a mutation handler that processes data before API submission
 * This can be used in components to wrap mutation calls
 */
export const createProcessedMutationHandler = <T extends BaseEntity, R = unknown>(
  mutationFn: (data: T) => Promise<R>,
  fieldsConfig: Record<string, SimpleFieldConfig>,
  currentUserId?: string
) => {
  return async (data: T): Promise<R> => {
    const processedData = processEntityDataForApi(data, fieldsConfig, currentUserId);
    return mutationFn(processedData);
  };
};

/**
 * Validate that required fields are present after processing
 * This can be used to ensure data integrity before API submission
 */
export const validateProcessedTaskData = <T extends BaseEntity>(
  item: T,
  fieldsConfig: Record<string, SimpleFieldConfig>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  Object.keys(fieldsConfig).forEach((fieldId) => {
    const fieldConfig = fieldsConfig[fieldId];
    const fieldValue = item[fieldId as keyof T];

    // Check required fields
    if (
      fieldConfig.required &&
      (fieldValue === undefined || fieldValue === null || fieldValue === "")
    ) {
      errors.push(`${fieldConfig.label || fieldId} is required`);
    }

    // Validate field-specific constraints
    switch (fieldConfig.type) {
      case "multi-user":
        if (Array.isArray(fieldValue) && fieldValue.length === 0 && fieldConfig.required) {
          errors.push(`${fieldConfig.label || fieldId} must have at least one user assigned`);
        }
        break;

      case "tags":
        if (Array.isArray(fieldValue) && fieldValue.length === 0 && fieldConfig.required) {
          errors.push(`${fieldConfig.label || fieldId} must have at least one tag`);
        }
        break;

      // Add more validation rules as needed
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
